import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a dividend in simple terms?',
    a: 'A dividend is a slice of a company’s profit paid directly to shareholders in cash. If you own 100 shares of a stock that pays $1.00 per share per year, you receive $100 a year from that company, usually split into four $25 payments. It is a cheque the company writes to you for being a partial owner.',
  },
  {
    q: 'Why do companies pay dividends?',
    a: 'Mature, profitable companies generate more cash than they can reinvest at high returns. Instead of letting cash pile up on the balance sheet or making bad acquisitions, they return the excess to shareholders. Paying a dividend is also a signal of confidence — management is telling the market the business is stable enough to commit to ongoing payments.',
  },
  {
    q: 'Do all stocks pay dividends?',
    a: 'No. Roughly 80% of S&P 500 companies pay a dividend, but young growth companies (think Tesla, Amazon for most of its history, Berkshire Hathaway) reinvest everything. As a rule of thumb, established companies in mature sectors — consumer staples, utilities, healthcare, financials, REITs — are where dividends concentrate.',
  },
  {
    q: 'How often are dividends paid?',
    a: 'In the US the default is quarterly (4× per year). A small but growing number of ETFs and REITs pay monthly (Realty Income / O, Main Street Capital / MAIN, JEPI, JEPQ). Some international stocks pay semi-annually or annually. DiviDrip’s Stock Screener has a Frequency filter so you can target any cadence.',
  },
  {
    q: 'Are dividends guaranteed?',
    a: 'No. A dividend is a discretionary payment the board of directors approves each quarter. They can cut or suspend it at any time — GE, AT&T, Walgreens, and Intel have all done so within the last few years. This is why dividend safety metrics (payout ratio, free cash flow coverage, dividend streak) matter so much.',
  },
  {
    q: 'How do I actually receive a dividend?',
    a: 'Cash dividends land in your brokerage account on the pay date (typically 2–4 weeks after the ex-dividend date) as a credit. You can choose to receive them as cash or enroll in DRIP — your broker’s Dividend Reinvestment Plan — which auto-buys more shares of the same stock with the dividend cash.',
  },
];

export default function LearnWhatIsADividend() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="What Is a Dividend? A Complete Beginner&apos;s Guide (2026) | DiviDrip"
      description="A true ground-zero explainer of what a dividend is, why companies pay them, who gets them, when, and how they show up in your account. Written for absolute beginners."
      canonical="https://twylightcrow.com/learn/what-is-a-dividend"
      guidePath="/learn/what-is-a-dividend"
      sectionLabel="DRIP & Reinvestment Basics"
      sectionAnchor="basics"
      kicker="Learn · The basics"
      h1="What Is a Dividend? A Complete Beginner&apos;s Guide"
      datePublished="2026-06-14"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/what-is-drip', label: 'What is DRIP? Dividend reinvestment explained' },
        { to: '/learn/ex-dividend-date-timeline', label: 'The ex-dividend date timeline, day-by-day' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/payout-ratio', label: 'The payout ratio — your first safety check' },
        { to: '/learn/four-phases', label: 'The 4 phases of a dividend investor’s life' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — starting with $10K' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        A dividend is a slice of a company&apos;s profit paid directly to you,
        the shareholder, in cash. That&apos;s the entire concept. Everything else
        on this page is color around that one sentence.
      </p>
      <p>
        Most guides assume you already know this. This one doesn&apos;t. If you
        have ever wondered <em>where the money actually comes from</em>, <em>why
        some stocks pay it and others don&apos;t</em>, and <em>what happens
        between &quot;company earns a profit&quot; and &quot;money lands in my
        brokerage account&quot;</em> &mdash; keep reading.
      </p>

      <h2>The 30-second version</h2>
      <ol>
        <li>You own shares of a company.</li>
        <li>The company earns a profit.</li>
        <li>The board of directors votes to pay part of that profit out to shareholders.</li>
        <li>A few weeks later, the cash shows up in your brokerage account.</li>
        <li>You can spend it, save it, or reinvest it into more shares via{' '}
          <Link to="/learn/what-is-drip">DRIP</Link>.</li>
      </ol>
      <p>
        Multiply that little payment by every shareholder of the company, several
        times a year, for decades, and you have one of the largest cash-flow
        engines in the world. In 2024 S&amp;P 500 companies paid out roughly
        $630&nbsp;billion in dividends. Multinational investors collected even more
        when you add European, Canadian, and Asian dividend stocks.
      </p>

      <h2>A worked example: Coca-Cola (KO)</h2>
      <p>
        Coca-Cola pays a dividend of about $2.04 per share per year as of 2026,
        split into four quarterly payments of $0.51. Say you bought 100 shares.
        Here is exactly what happens over a year:
      </p>
      <ul>
        <li>April: $0.51 &times; 100 shares = <strong>$51 deposited</strong> into your brokerage.</li>
        <li>July: $51 again.</li>
        <li>October: $51 again.</li>
        <li>December: $51 again.</li>
        <li>Annual total: <strong>$204</strong> in cash, just for owning the stock.</li>
      </ul>
      <p>
        You did not have to sell anything. You did not have to time the market.
        The cash arrived four times because Coca-Cola, like ~80% of S&amp;P 500
        members, has decided that returning excess profit to shareholders is the
        best use of that money. Coca-Cola has done this for{' '}
        <Link to="/learn/dividend-kings-vs-aristocrats-vs-achievers">62 consecutive years</Link>
        {' '}without a cut &mdash; a Dividend King.
      </p>

      <h2>Why companies pay dividends (and why some don&apos;t)</h2>
      <p>
        Every dollar a company earns goes to one of five places:
      </p>
      <ol>
        <li><strong>Reinvest in the business</strong> &mdash; build a factory, launch a product, expand to a new country.</li>
        <li><strong>Acquire other companies.</strong></li>
        <li><strong>Buy back its own shares</strong> &mdash; shrinks the share count, lifts EPS.</li>
        <li><strong>Pay down debt.</strong></li>
        <li><strong>Pay a dividend.</strong></li>
      </ol>
      <p>
        Young growth companies pick (1) and (2). They can find investments inside
        the business that return 20&ndash;30% a year &mdash; better than they could
        deliver to a shareholder. Tesla, Amazon for most of its life, Google for
        most of its life, Berkshire Hathaway forever &mdash; none of them paid a
        meaningful dividend during their high-growth years.
      </p>
      <p>
        Mature companies pick (5) because they cannot find enough high-return
        projects to soak up every dollar of profit. Coca-Cola, Procter &amp; Gamble,
        Johnson &amp; Johnson, McDonald&apos;s &mdash; their products are
        well-established, capital needs are modest, and the cash piles up faster
        than they can use it. So they return it to shareholders. This is healthy:
        you do not want a mature company building a factory it doesn&apos;t need
        just to spend the cash.
      </p>

      <h2>The five dates that confuse every beginner</h2>
      <p>
        A dividend payment is not instant. There are five events between
        announcement and cash landing, and conflating them is the most common
        beginner mistake.
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>What happens</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Declaration date</strong></td>
            <td>The board announces &quot;we are paying $0.51 per share to whoever owns the stock on the record date.&quot;</td>
          </tr>
          <tr>
            <td><strong>Ex-dividend date</strong></td>
            <td>The first day the stock trades <em>without</em> the right to the next dividend. Buy on or after ex-date and you do <em>not</em> get this dividend. The price typically drops by about the dividend amount overnight.</td>
          </tr>
          <tr>
            <td><strong>Record date</strong></td>
            <td>The company looks at its shareholder register on this date. Whoever is on the list gets paid. Usually 1&ndash;2 business days after the ex-date.</td>
          </tr>
          <tr>
            <td><strong>Pay date</strong></td>
            <td>The day the cash actually arrives in your brokerage account. Usually 2&ndash;4 weeks after the ex-date.</td>
          </tr>
          <tr>
            <td><strong>Settlement</strong></td>
            <td>The cash is officially yours and can be withdrawn or reinvested. Usually same-day as pay date now that the US runs on T+1 settlement.</td>
          </tr>
        </tbody>
      </table>
      <p>
        The trap: buying a stock the day <em>before</em> the ex-date does
        <em> not</em> give you free money. The price drops by the dividend
        amount on the morning of the ex-date, so you net to zero (minus the tax
        bill). The full timeline is in our{' '}
        <Link to="/learn/ex-dividend-date-timeline">ex-dividend date timeline guide</Link>.
      </p>

      <h2>How often do dividends arrive?</h2>
      <p>
        Most US companies pay <strong>quarterly</strong> &mdash; four times a year.
        That cadence dates to railroad and bank dividends in the 1800s and never
        changed. Other patterns:
      </p>
      <ul>
        <li><strong>Monthly:</strong> Realty Income (O), Main Street Capital (MAIN), JEPI, JEPQ, AGNC, STAG. Popular for retirees who want a regular paycheck rhythm.</li>
        <li><strong>Semi-annual:</strong> Many European and Asian dividend stocks (Unilever, Nestl&eacute;).</li>
        <li><strong>Annual:</strong> Some Japanese companies, plus US special dividends.</li>
        <li><strong>Weekly:</strong> Very rare &mdash; a handful of newer covered-call ETFs.</li>
      </ul>
      <p>
        See <Link to="/learn/monthly-vs-quarterly-dividends">monthly vs quarterly</Link> for
        the trade-off, and <Link to="/learn/monthly-dividend-calendar">the monthly
        dividend calendar trick</Link> for combining the two into a year-round
        cash flow.
      </p>

      <h2>Cash dividends vs stock dividends vs special dividends</h2>
      <ul>
        <li>
          <strong>Cash dividends</strong> &mdash; the default and the only kind 95%
          of investors care about. Cash hits your brokerage account.
        </li>
        <li>
          <strong>Stock dividends</strong> &mdash; the company gives you extra
          shares instead of cash (usually a small % like 2&ndash;5%). Rare. Functions
          like a stock split.
        </li>
        <li>
          <strong>Special dividends</strong> &mdash; a one-time large payment funded
          by an asset sale, windfall, or excess balance sheet. Costco has paid
          three (the most famous was a $15/share special in 2024). Special
          dividends distort dividend-growth math; see{' '}
          <Link to="/learn/special-dividends-in-cagr-math">special dividends and CAGR math</Link>{' '}
          for how DiviDrip flags them.
        </li>
      </ul>

      <h2>What about taxes?</h2>
      <p>
        In the US, dividends are taxable in the year you receive them &mdash;
        unless they&apos;re inside a tax-advantaged account (Roth IRA, Traditional
        IRA, 401(k), HSA). The IRS splits cash dividends into two buckets:
      </p>
      <ul>
        <li>
          <strong>Qualified dividends</strong> are taxed at the lower long-term
          capital gains rates (0%, 15%, or 20% depending on income). Most US
          C-corporation dividends are qualified if you held the stock for more
          than 60 days around the ex-date.
        </li>
        <li>
          <strong>Ordinary (non-qualified) dividends</strong> are taxed at your
          regular income rate (10&ndash;37%). REIT and BDC dividends, plus
          short-holding-period dividends, fall here.
        </li>
      </ul>
      <p>
        For the full picture see{' '}
        <Link to="/learn/qualified-vs-ordinary-dividends">qualified vs ordinary dividends</Link>.
        Foreign dividends have their own
        {' '}<Link to="/learn/international-dividends">withholding-tax wrinkle</Link>.
      </p>

      <h2>How to find your first dividend stocks</h2>
      <ol>
        <li>
          Open the <Link to="/screener">DiviDrip Screener</Link>. It lists 4,500+
          dividend-paying US stocks and ETFs.
        </li>
        <li>
          Pick a Tier filter &mdash; Dividend Kings (50+ years of raises) or
          Aristocrats (25+ years) are the safest starting universe.
        </li>
        <li>
          Set a Yield range. 2&ndash;5% is the sweet spot for healthy long-term
          dividend payers; under 2% is too low to feel; over 7% is a yellow flag.
        </li>
        <li>
          Click any ticker to open the Stock Modal. The Dividend Info tab shows
          the current yield, payout ratio, and dividend streak. The Triangle tab
          gives you a single safety score.
        </li>
      </ol>
      <p>
        You don&apos;t need to be an expert. The screener and Triangle do the
        filtering work; your job is to pick names that fit your timeline. See
        our <Link to="/learn/first-10-tickers">first 10 tickers</Link> guide for
        a worked starter portfolio with $10K.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, add any dividend stock you
        already own (or any name from our <Link to="/learn/first-10-tickers">first
        10 tickers</Link>), and you&apos;ll see the next dividend pay date, the
        amount you&apos;ll receive, and the cumulative income you&apos;ll have
        collected by year-end. Tracking a single position for one quarter is
        worth more than reading ten beginner guides &mdash; the rhythm of
        dividend investing only makes sense once you&apos;ve watched cash land
        in your account.
      </p>
    </LearnPage>
  );
}
